import React, { ReactNode, useCallback, useMemo } from 'react';

import { Search } from 'lucide-react';
import UDInput from 'ud-form/components/input/component';
import { UDSelect } from 'ud-form/components/select/component';
import { SelectOption } from 'ud-form/components/select/component/types';
import UDPerPage from 'ud-ui/components/per-page/per-page';
import debounce from 'shared/utils/debounce';
import { theme } from 'styles/theme/theme';
import { FilterConfig, FilterValues, SortConfig, SortValue, TextFilterConfig } from 'ud-ui/components/category/domain/types';

interface TextFilterInputProps {
  config: TextFilterConfig;
  value: string;
  onFilterChange: (key: string, value: string | null) => void;
}

const TextFilterInput = React.memo(({ config, value, onFilterChange }: TextFilterInputProps) => {
  const handleChange = useMemo(
    () =>
      debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        onFilterChange(config.key, e.target.value || null);
      }, config.debounceMs ?? 300),
    [config.key, config.debounceMs, onFilterChange],
  );

  return (
    <UDInput
      value={value}
      placeholder={config.placeholder}
      onChange={handleChange}
      iconProps={{
        position: 'icon-left',
        component: <Search size={16} color={theme.colors.SurfaceRomanSilver30} />,
      }}
    />
  );
});

interface CategoryFiltersProps {
  filters?: FilterConfig[];
  filterValues: FilterValues;
  onFilterChange: (key: string, value: string | string[] | null) => void;
  sortConfig?: SortConfig;
  sortValue?: SortValue;
  onSortChange?: (sort: SortValue) => void;
  extraFilters?: ReactNode;
  perPage: number;
  onChangePerPage?: (value: number) => void;
  legacyFilter?: string | null;
  legacyPlaceholder?: string;
  onLegacyFilterChange?: (value: string) => void;
}

function CategoryFilters(props: CategoryFiltersProps) {
  const {
    filters,
    filterValues,
    onFilterChange,
    sortConfig,
    sortValue,
    onSortChange,
    extraFilters,
    perPage,
    onChangePerPage,
    legacyFilter,
    legacyPlaceholder,
    onLegacyFilterChange,
  } = props;

  const handleLegacySearch = useMemo(
    () =>
      debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        onLegacyFilterChange?.(e.target.value);
      }, 300),
    [onLegacyFilterChange],
  );

  const handleChangePerPage = useCallback(
    (value: number) => {
      onChangePerPage?.(value);
    },
    [onChangePerPage],
  );

  const handleSortFieldChange = useCallback(
    (option: SelectOption | null) => {
      if (!option || !onSortChange) return;
      onSortChange({ field: option.value, direction: sortValue?.direction ?? 'asc' });
    },
    [onSortChange, sortValue?.direction],
  );

  const handleSortDirectionToggle = useCallback(() => {
    if (!onSortChange || !sortValue) return;
    onSortChange({ field: sortValue.field, direction: sortValue.direction === 'asc' ? 'desc' : 'asc' });
  }, [onSortChange, sortValue]);

  const sortFieldValue = useMemo(
    () => sortConfig?.options.find((o) => o.value === sortValue?.field) ?? null,
    [sortConfig?.options, sortValue?.field],
  );

  const renderFilters = () => {
    if (!filters) {
      return (
        <div className="col-5">
          <UDInput
            value={legacyFilter ?? ''}
            placeholder={legacyPlaceholder}
            onChange={handleLegacySearch}
            iconProps={{
              position: 'icon-left',
              component: <Search size={16} color={theme.colors.SurfaceRomanSilver30} />,
            }}
          />
        </div>
      );
    }

    return (
      <>
        {filters.map((config) => {
          if (config.type === 'text') {
            return (
              <div key={config.key} className="mr-2">
                <TextFilterInput
                  config={config}
                  value={(filterValues[config.key] as string) ?? ''}
                  onFilterChange={onFilterChange}
                />
              </div>
            );
          }

          if (config.type === 'select') {
            const rawValue = filterValues[config.key];
            const selectValue = config.isMulti
              ? config.options.filter((o) => Array.isArray(rawValue) && rawValue.includes(o.value))
              : (config.options.find((o) => o.value === rawValue) ?? null);

            const handleSelectChange = (selected: SelectOption | readonly SelectOption[] | null) => {
              if (selected === null) {
                onFilterChange(config.key, null);
              } else if (Array.isArray(selected)) {
                onFilterChange(config.key, (selected as SelectOption[]).map((o) => o.value));
              } else {
                onFilterChange(config.key, (selected as SelectOption).value);
              }
            };

            return (
              <div key={config.key} className="mr-2">
                <UDSelect
                  options={config.options}
                  value={selectValue}
                  placeholder={config.placeholder}
                  isMulti={config.isMulti ?? false}
                  isClearable
                  onChange={handleSelectChange}
                />
              </div>
            );
          }

          return null;
        })}
      </>
    );
  };

  return (
    <div className="d-flex justify-content-between mb-6">
      <div className="d-flex align-items-center flex-wrap">
        {renderFilters()}
        {extraFilters}
        {sortConfig && (
          <>
            <div className="mr-2">
              <UDSelect
                options={sortConfig.options}
                value={sortFieldValue}
                isMulti={false}
                onChange={handleSortFieldChange}
              />
            </div>
            <button
              type="button"
              onClick={handleSortDirectionToggle}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: '0 4px' }}
            >
              {sortValue?.direction === 'desc' ? '↓' : '↑'}
            </button>
          </>
        )}
      </div>
      <UDPerPage value={perPage} onChange={handleChangePerPage} />
    </div>
  );
}

export default React.memo(CategoryFilters);
